import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Menu, X, Calendar, Truck, Package, ClipboardList,
  Home, ChevronLeft
} from 'lucide-react';

// Define consistent theme colors
const THEME_COLORS = {
  inbound: 'blue-600',
  outbound: 'green-600',
  default: 'slate-700'
};

const TruckSchedulerSidebar = ({ mode }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Generate inbound or outbound paths based on current mode
  const getCurrentMode = () => {
    const effectiveMode = mode || (location.pathname.includes('truck_scheduler/outbound') ? 'outbound' : 'inbound');
    return effectiveMode;
  };

  const getBasePath = (specificMode) => {
    const modeToUse = specificMode || getCurrentMode();
    return `/tiles/truck_scheduler/${modeToUse}`;
  };

  // Get theme color class for current mode
  const getThemeColor = () => {
    const currentMode = getCurrentMode();
    return THEME_COLORS[currentMode] || THEME_COLORS.default;
  };

  const navigationItems = [
    {
      category: 'INBOUND OPERATIONS',
      mode: 'inbound',
      items: [
        { name: 'Inbound Calendar', path: '/tiles/truck_scheduler/inbound/calendar', icon: <Calendar className="w-5 h-5" /> },
        { name: 'Receiving Operations', path: '/tiles/truck_scheduler/inbound/receiving', icon: <Truck className="w-5 h-5" /> },
        { name: 'Inbound History', path: '/tiles/truck_scheduler/inbound/history', icon: <ClipboardList className="w-5 h-5" /> }
      ]
    },
    {
      category: 'OUTBOUND OPERATIONS',
      mode: 'outbound',
      items: [
        { name: 'Outbound Calendar', path: '/tiles/truck_scheduler/outbound/calendar', icon: <Calendar className="w-5 h-5" /> },
        { name: 'Outbound Operations', path: '/tiles/truck_scheduler/outbound/shipping', icon: <Package className="w-5 h-5" /> },
        { name: 'Outbound History', path: '/tiles/truck_scheduler/outbound/history', icon: <ClipboardList className="w-5 h-5" /> }
      ]
    }
  ];

  const isActive = (path) => {
    // Handle both absolute and relative paths
    if (path.startsWith('/')) {
      return location.pathname === path;
    } else {
      // For relative paths, check if current path ends with the provided path
      return location.pathname.endsWith('/' + path);
    }
  };

  const getActiveColor = (path) => {
    if (!isActive(path)) return 'text-slate-300 hover:bg-slate-700/50 hover:text-slate-100';
    
    const currentMode = getCurrentMode();
    const themeColor = THEME_COLORS[currentMode] || THEME_COLORS.default;
    
    return `bg-${themeColor} text-white`;
  };

  return (
    <>
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-md bg-slate-800 text-slate-200"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      >
        {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <div
        className={`fixed inset-y-0 left-0 z-40 transition-transform duration-300 transform 
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          h-screen bg-slate-800 border-r border-slate-700 w-64 overflow-y-auto`}
      >
        <div className="p-4 border-b border-slate-700/50">
          <Link 
            to="/"
            className="flex items-center px-4 py-2 text-sm text-slate-300 hover:bg-slate-700/50 hover:text-slate-100 rounded-md"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            <span>Back to Home</span>
          </Link>
        </div>

        <nav className="mt-4">
          {navigationItems.map((section) => (
            <div key={section.category} className="mb-6">
              <h3 className="px-4 mb-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                {section.category}
              </h3>
              <div>
                {section.items.map(item => (
                  <Link 
                    key={item.path} 
                    to={item.path}
                    className={`flex items-center px-4 py-2 text-sm h-10 ${getActiveColor(item.path)}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span className="mr-3">{item.icon}</span>
                    <span>{item.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </div>
    </>
  );
};

export default TruckSchedulerSidebar;